import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/global';
import ConfirmationModal from '../modal/ConfirmationModal';
import useCampaignEditor from '../../hooks/marketing/useCampaignEditor';

// Fires the post-offboarding "start a campaign?" prompt declaratively from
// router state, so the offboarding wizard can navigate away first and the
// prompt overlays the team page rather than the wizard.
export type OffboardingCampaignPromptState = {
  offboardingId: string;
  employeeName: string;
};

function readPromptState(
  state: unknown,
): OffboardingCampaignPromptState | null {
  if (!state || typeof state !== 'object') return null;
  const prompt = (state as { offboardingCampaignPrompt?: unknown })
    .offboardingCampaignPrompt;
  if (!prompt || typeof prompt !== 'object') return null;
  const { offboardingId, employeeName } = prompt as Record<string, unknown>;
  if (typeof offboardingId !== 'string' || typeof employeeName !== 'string') {
    return null;
  }
  return { offboardingId, employeeName };
}

function OffboardingCampaignPromptHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const editor = useCampaignEditor();
  const prompt = readPromptState(location.state);

  const clear = () => navigate(ROUTES.team, { replace: true });

  if (!prompt) return null;

  const onStart = () => {
    editor.open({ offboardingContext: prompt });
    // Clear router state so a back-nav or refresh doesn't re-prompt; the
    // editor is now owned by the provider's single mount.
    clear();
  };

  return (
    <ConfirmationModal
      open
      handleClose={clear}
      title="Reach out to affected customers?"
      message={`If ${prompt.employeeName} has customers with appointments after the offboarding takes effect, you can send them a campaign to let them know about the change.`}
      positiveText="Start campaign"
      negativeText="Not now"
      positiveAction={onStart}
      negativeAction={clear}
    />
  );
}

export default OffboardingCampaignPromptHandler;
